import { OrderLaunchedFrom } from 'models/purchaseFlow/orderLaunchedFrom'
import { PropertyReorderContextDTO } from 'models/property'
import { logger } from 'utils/logging'

export const REORDER_CONTEXT_WAIT_MS = 2000

export interface ReorderClickProps {
  launched_from: OrderLaunchedFrom
  order_sequence_for_property: number | null
  previous_order_id: number | null
  reorder_by_different_broker: boolean | null
}

/** The order click's re-order props; an unknown context is reported as null, never as a first order. */
export const reorderClickProps = (launchedFrom: OrderLaunchedFrom, context: PropertyReorderContextDTO | null): ReorderClickProps => ({
  launched_from: launchedFrom,
  order_sequence_for_property: context?.orderSequenceForProperty ?? null,
  previous_order_id: context?.previousOrderId ?? null,
  reorder_by_different_broker: context?.reorderByDifferentBroker ?? null,
})

/** The embed adds the OneSystem allowance: the purchase only applies it to an order placed through Propstack. */
export const embedReorderClickProps = (launchedFrom: OrderLaunchedFrom, context: PropertyReorderContextDTO | null) => ({
  ...reorderClickProps(launchedFrom, context),
  free_photos_remaining_at_start: context?.freePhotosRemainingAtStart ?? null,
})

/** The context for a click: known at once when the render already cached it, else settled after the read. */
export type SettledReorderContext = PropertyReorderContextDTO | null | Promise<PropertyReorderContextDTO | null>

const isPending = <T>(value: T | Promise<T>): value is Promise<T> => typeof (value as Promise<T> | null)?.then === 'function'

let waitWarned = false

/**
 * Settles the context for a click. A context the render already cached passes through at once, so the click logs
 * before the flow opens; a pending read resolves to null when it fails (the query logged it) or outlasts the wait,
 * so a slow backend never holds the click event back for long. Never rejects.
 */
export const settleReorderContext = (pending: PropertyReorderContextDTO | Promise<PropertyReorderContextDTO>, waitMs = REORDER_CONTEXT_WAIT_MS): SettledReorderContext => {
  if (!isPending(pending)) return pending

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      if (!waitWarned) {
        waitWarned = true
        logger.warn('Re-order context not ready for the order click', undefined, {
          context: { component: 'reorderClick', action: 'reorderContextTimedOut', metadata: { waitMs } },
        })
      }
      resolve(null)
    }, waitMs)
    pending.then(
      (context) => { clearTimeout(timer); resolve(context) },
      () => { clearTimeout(timer); resolve(null) }
    )
  })
}

/** Mixpanel's own event-time property, in epoch seconds like its SDK's default. */
export interface ClickTime {
  time: number
}

/**
 * Runs `fn` at once for a known context, after the read for a pending one. Either way it hands over the click's time,
 * so a click event logged after the read still sorts before the flow events the click opened.
 */
export const withReorderContext = (settled: SettledReorderContext, fn: (context: PropertyReorderContextDTO | null, clickTime: ClickTime) => void): void => {
  const clickTime = { time: Date.now() / 1000 }
  if (isPending(settled)) void settled.then((context) => fn(context, clickTime))
  else fn(settled, clickTime)
}
